import { useState } from 'react';
import { useQuiz } from '../../hooks/useQuiz';
import { useAuth } from '../../context/Auth/auth-context';
import { toEmbedUrl } from '../../utils/youtube';
import { generateQuiz, getSavedGeminiKeys, saveGeminiKeys, parseKeys } from '../../services/quiz-ai';

// Authoring flow:
//   1. COMPOSE — generate in-app via Gemini (BYO AI Studio key), or copy an
//                LLM prompt (schema baked in) → paste the generated JSON.
//   2. EDIT    — a structured editor where the human adds what an LLM can't supply:
//                real photo URLs and embedded YouTube clips, plus final tweaks.
// The editor guarantees a well-formed quiz before it ever hits createQuiz.

const EXAMPLE = `{
  "questions": [
    {
      "questionText": "What is the capital of France?",
      "options": [
        { "optionText": "Paris", "isCorrect": true },
        { "optionText": "Lyon", "isCorrect": false },
        { "optionText": "Marseille", "isCorrect": false },
        { "optionText": "Nice", "isCorrect": false }
      ],
      "timer": 20
    }
  ]
}`;

interface EditOption {
  optionText: string;
  isCorrect: boolean;
}
interface EditQuestion {
  questionText: string;
  questionImage: string;
  questionVideo: string;
  options: EditOption[];
  timer: number;
}

const emptyQuestion = (): EditQuestion => ({
  questionText: '',
  questionImage: '',
  questionVideo: '',
  timer: 20,
  options: [
    { optionText: '', isCorrect: true },
    { optionText: '', isCorrect: false },
  ],
});

const isTrue = (v: any) =>
  v === true || (typeof v === 'string' && v.toLowerCase() === 'true');

// Lenient import: accept whatever an LLM produced and let the editor's save
// validation enforce correctness. Shared by the paste path and the in-app
// Gemini path.
const toEditModel = (qs: any[]): EditQuestion[] =>
  qs.map((q: any) => {
    const options: EditOption[] = (Array.isArray(q?.options) ? q.options : []).map(
      (o: any) => ({
        optionText: typeof o === 'string' ? o : o?.optionText ?? '',
        isCorrect: typeof o === 'string' ? false : isTrue(o?.isCorrect),
      })
    );
    while (options.length < 2) options.push({ optionText: '', isCorrect: false });
    // guarantee at most one correct flag survives import
    let seen = false;
    for (const o of options) {
      if (o.isCorrect && !seen) seen = true;
      else o.isCorrect = false;
    }
    return {
      questionText: q?.questionText ?? '',
      questionImage: q?.questionImage ?? '',
      questionVideo: q?.questionVideo ?? '',
      timer: Number(q?.timer) || 20,
      options: options.slice(0, 4),
    };
  });

export const CreateQuiz = () => {
  const { auth } = useAuth();
  const { createQuiz, loading, error } = useQuiz();
  const [step, setStep] = useState<'compose' | 'edit'>('compose');
  const [quizName, setQuizName] = useState('');

  // compose step
  const [topic, setTopic] = useState('');
  const [count, setCount] = useState(8);
  const [rawJson, setRawJson] = useState('');
  const [promptCopied, setPromptCopied] = useState(false);

  // in-app Gemini generation
  const [content, setContent] = useState('');
  const [grounded, setGrounded] = useState(false);
  const [keysBlob, setKeysBlob] = useState(getSavedGeminiKeys);
  const [keysOpen, setKeysOpen] = useState(() => !getSavedGeminiKeys());
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [genModel, setGenModel] = useState('');

  // edit step
  const [questions, setQuestions] = useState<EditQuestion[]>([]);

  const [parseError, setParseError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // --- compose: the copyable prompt (schema baked in) ----------------------
  const buildPrompt = () => {
    const n = Number(count) || 8;
    return `I'm making a live trivia quiz${topic.trim() ? ` about "${topic.trim()}"` : ''}. Generate ${n} questions.

Return ONLY valid JSON — no markdown fences, no commentary — in EXACTLY this shape:

{
  "questions": [
    {
      "questionText": "the question",
      "options": [
        { "optionText": "an answer", "isCorrect": true },
        { "optionText": "another answer", "isCorrect": false }
      ],
      "timer": 20
    }
  ]
}

Rules:
- 2 to 4 options per question.
- Exactly ONE option per question has "isCorrect": true; all others false.
- "timer" is the seconds players get to answer (use 15–30).
- Do NOT add image or video fields — I'll add those myself afterwards.
- Keep questions punchy, factual, and unambiguous.`;
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(buildPrompt());
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 1800);
    } catch {
      /* clipboard blocked — the prompt is visible below to copy manually */
    }
  };

  // --- compose -> edit: parse leniently, let the editor enforce correctness -
  const loadIntoEditor = () => {
    setParseError('');
    let data: any;
    try {
      data = JSON.parse(rawJson);
    } catch {
      setParseError('That JSON is invalid — check for trailing commas or missing quotes.');
      return;
    }
    const qs = data?.questions;
    if (!Array.isArray(qs) || qs.length === 0) {
      setParseError('JSON must have a non-empty "questions" array.');
      return;
    }
    setGenModel('');
    setQuestions(toEditModel(qs));
    setStep('edit');
  };

  // --- in-app generation: Gemini chain with key-pool rotation ---------------
  const handleGenerate = async () => {
    setParseError('');
    setGenError('');
    const apiKeys = parseKeys(keysBlob);
    if (apiKeys.length === 0) {
      setGenError('Paste your (free) Google AI Studio key first — aistudio.google.com/apikey.');
      setKeysOpen(true);
      return;
    }
    setGenerating(true);
    try {
      const { questions: qs, model } = await generateQuiz({
        topic,
        content,
        count: Number(count) || 8,
        grounded,
        apiKeys,
      });
      saveGeminiKeys(keysBlob); // only persist keys that actually worked
      if (!quizName.trim() && topic.trim()) setQuizName(topic.trim());
      setGenModel(model);
      setQuestions(toEditModel(qs));
      setStep('edit');
    } catch (e: any) {
      setGenError(e?.message || 'Generation failed — try again.');
    } finally {
      setGenerating(false);
    }
  };

  const startBlank = () => {
    setParseError('');
    setQuestions([emptyQuestion()]);
    setStep('edit');
  };

  // --- edit mutators --------------------------------------------------------
  const patchQuestion = (qi: number, patch: Partial<EditQuestion>) =>
    setQuestions((prev) => prev.map((q, i) => (i === qi ? { ...q, ...patch } : q)));

  const patchOption = (qi: number, oi: number, text: string) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qi
          ? { ...q, options: q.options.map((o, j) => (j === oi ? { ...o, optionText: text } : o)) }
          : q
      )
    );

  const setCorrect = (qi: number, oi: number) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qi
          ? { ...q, options: q.options.map((o, j) => ({ ...o, isCorrect: j === oi })) }
          : q
      )
    );

  const addOption = (qi: number) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qi && q.options.length < 4
          ? { ...q, options: [...q.options, { optionText: '', isCorrect: false }] }
          : q
      )
    );

  const removeOption = (qi: number, oi: number) =>
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qi || q.options.length <= 2) return q;
        const options = q.options.filter((_, j) => j !== oi);
        if (!options.some((o) => o.isCorrect)) options[0].isCorrect = true; // never orphan the answer
        return { ...q, options };
      })
    );

  const addQuestion = () => setQuestions((prev) => [...prev, emptyQuestion()]);
  const removeQuestion = (qi: number) =>
    setQuestions((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== qi)));

  // --- save -----------------------------------------------------------------
  const handleCreate = async () => {
    setParseError('');
    if (!quizName.trim()) {
      setParseError('Give the quiz a name.');
      return;
    }
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.questionText.trim()) {
        setParseError(`Question ${i + 1} needs question text.`);
        return;
      }
      const filled = q.options.filter((o) => o.optionText.trim());
      if (filled.length < 2) {
        setParseError(`Question ${i + 1} needs at least 2 options.`);
        return;
      }
      if (q.options.filter((o) => o.isCorrect && o.optionText.trim()).length !== 1) {
        setParseError(`Question ${i + 1}: mark exactly one (non-empty) option correct.`);
        return;
      }
    }

    const payload = questions.map((q) => ({
      questionText: q.questionText.trim(),
      questionImage: q.questionImage.trim(),
      questionVideo: q.questionVideo.trim(),
      timer: q.timer,
      options: q.options
        .filter((o) => o.optionText.trim())
        .map((o) => ({ optionText: o.optionText.trim(), isCorrect: o.isCorrect })),
    }));

    const id = await createQuiz({
      title: quizName.trim(),
      createdBy: auth?.currentUser?.uid || '',
      questions: payload,
    });

    if (id) {
      setQuizName('');
      setRawJson('');
      setTopic('');
      setQuestions([]);
      setStep('compose');
      setSuccessMessage('Quiz created! Find it under Quizzes to host a live game.');
      setTimeout(() => setSuccessMessage(''), 4000);
    }
  };

  // =========================================================================
  if (step === 'edit') {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-extrabold font-display tracking-tight">Review &amp; edit</h1>
          <button
            onClick={() => setStep('compose')}
            className="btn-ghost text-sm !text-ink !bg-ink/10 hover:!bg-ink/15"
          >
            ← Back
          </button>
        </div>
        {genModel && (
          <p className="text-sm text-slate-500">
            Generated by <span className="font-semibold text-ink">{genModel}</span> — double-check
            the marked answers before creating.
          </p>
        )}

        <div className="card p-5">
          <label htmlFor="quizName" className="block mb-1 font-medium">Quiz name</label>
          <input
            id="quizName"
            type="text"
            value={quizName}
            onChange={(e) => setQuizName(e.target.value)}
            placeholder="e.g. Fun Friday — General Knowledge"
            className="w-full p-3 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none"
          />
        </div>

        {questions.map((q, qi) => (
          <div key={qi} className="card p-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                Question {qi + 1}
              </span>
              <button
                onClick={() => removeQuestion(qi)}
                disabled={questions.length <= 1}
                className="text-tile-red text-sm hover:underline disabled:text-slate-300 disabled:no-underline"
              >
                Remove
              </button>
            </div>

            <textarea
              value={q.questionText}
              onChange={(e) => patchQuestion(qi, { questionText: e.target.value })}
              placeholder="Question text"
              className="w-full p-3 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none font-medium"
              rows={2}
            />

            {/* media — the bits the LLM can't fill in */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="url"
                value={q.questionImage}
                onChange={(e) => patchQuestion(qi, { questionImage: e.target.value })}
                placeholder="Image URL (optional)"
                className="w-full p-2.5 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none text-sm"
              />
              <input
                type="url"
                value={q.questionVideo}
                onChange={(e) => patchQuestion(qi, { questionVideo: e.target.value })}
                placeholder="YouTube URL (optional)"
                className="w-full p-2.5 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none text-sm"
              />
            </div>
            {q.questionVideo.trim() && !toEmbedUrl(q.questionVideo) && (
              <p className="text-xs text-tile-gold-deep">
                Couldn’t parse that YouTube URL — players will get a plain link instead of an embed.
              </p>
            )}

            {/* options — radio marks the single correct answer */}
            <div className="space-y-2">
              {q.options.map((o, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`correct-${qi}`}
                    checked={o.isCorrect}
                    onChange={() => setCorrect(qi, oi)}
                    title="Mark correct"
                    className="w-4 h-4 accent-tile-green shrink-0"
                  />
                  <input
                    type="text"
                    value={o.optionText}
                    onChange={(e) => patchOption(qi, oi, e.target.value)}
                    placeholder={`Option ${oi + 1}`}
                    className={`flex-1 p-2.5 rounded-xl border-2 focus:outline-none text-sm ${
                      o.isCorrect ? 'border-tile-green bg-tile-green/10' : 'border-ink/15 focus:border-ink'
                    }`}
                  />
                  <button
                    onClick={() => removeOption(qi, oi)}
                    disabled={q.options.length <= 2}
                    className="text-slate-400 hover:text-tile-red disabled:text-slate-200 px-1"
                    title="Remove option"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {q.options.length < 4 && (
                <button onClick={() => addOption(qi)} className="text-sm text-ink font-semibold underline decoration-2 decoration-accent underline-offset-2">
                  + Add option
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-500">Timer (s)</label>
              <input
                type="number"
                min={5}
                max={120}
                value={q.timer}
                onChange={(e) => patchQuestion(qi, { timer: Number(e.target.value) || 20 })}
                className="w-20 p-2 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none text-sm"
              />
              <span className="text-xs text-slate-400">App mode only — stream mode uses the host’s answer window.</span>
            </div>
          </div>
        ))}

        <button onClick={addQuestion} className="btn-ghost w-full !text-ink !bg-ink/10 hover:!bg-ink/15">
          + Add question
        </button>

        {(parseError || error) && (
          <p className="text-tile-red font-medium" role="alert">{parseError || error}</p>
        )}
        {successMessage && <p className="text-tile-green-deep font-medium" role="alert">{successMessage}</p>}

        <button onClick={handleCreate} disabled={loading} className="btn-primary w-full text-lg" aria-busy={loading}>
          {loading ? 'Creating…' : `Create quiz (${questions.length} question${questions.length === 1 ? '' : 's'})`}
        </button>
      </div>
    );
  }

  // === COMPOSE =============================================================
  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-4">
      <h1 className="text-2xl font-extrabold font-display tracking-tight">Create a quiz</h1>

      {/* In-app AI generation — Gemini free tier, BYO key(s). */}
      <div className="card p-6 space-y-3">
        <h2 className="font-bold">1 · Generate with AI</h2>
        <p className="text-sm text-slate-500">
          Pick a topic (and optionally paste source material) — questions land in the editor for
          review. You’ll add photos and videos in the next step.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Topic (e.g. 90s Bollywood)"
            className="sm:col-span-2 w-full p-2.5 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none text-sm"
          />
          <input
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) => setCount(Number(e.target.value) || 8)}
            placeholder="# questions"
            className="w-full p-2.5 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none text-sm"
          />
        </div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Optional: paste notes / an article — questions will be drawn only from it"
          rows={3}
          className="w-full p-2.5 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none text-sm"
        />
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={grounded}
            onChange={(e) => setGrounded(e.target.checked)}
            className="w-4 h-4 accent-tile-green"
          />
          Verify facts with Google Search
          <span className="text-xs text-slate-400">(smaller daily quota — use for current events)</span>
        </label>

        {/* BYO key(s): held in this browser only — never in the app bundle or the database. */}
        {keysOpen ? (
          <div className="space-y-1.5">
            <textarea
              value={keysBlob}
              onChange={(e) => setKeysBlob(e.target.value)}
              placeholder={'Google AI Studio API key — free at aistudio.google.com/apikey\nPaste several (one per line) to pool daily quota'}
              rows={2}
              spellCheck={false}
              className="w-full p-2.5 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none text-xs font-mono"
            />
            <p className="text-xs text-slate-400">
              Keys stay in this browser (localStorage) — they’re never uploaded anywhere except to
              Google when generating.
            </p>
          </div>
        ) : (
          <p className="text-xs text-slate-400">
            {parseKeys(keysBlob).length} API key{parseKeys(keysBlob).length === 1 ? '' : 's'} saved in this
            browser ·{' '}
            <button type="button" onClick={() => setKeysOpen(true)} className="font-semibold text-ink underline decoration-2 decoration-accent underline-offset-2">
              change
            </button>
          </p>
        )}

        {genError && <p className="text-tile-red font-medium text-sm" role="alert">{genError}</p>}
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="btn-primary w-full disabled:opacity-60"
          aria-busy={generating}
        >
          {generating ? 'Generating… (can take ~20s)' : `Generate ${Number(count) || 8} questions →`}
        </button>
      </div>

      {/* Manual path: copy the prompt into any chatbot, paste the JSON back. */}
      <div className="card p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-bold">2 · Or paste JSON from your own chatbot</h2>
          <button type="button" onClick={() => setRawJson(EXAMPLE)} className="text-sm text-ink font-semibold underline decoration-2 decoration-accent underline-offset-2">
            Insert example
          </button>
        </div>
        <details className="text-sm">
          <summary className="cursor-pointer text-slate-500 select-none">
            Copy the prompt for ChatGPT / Claude (uses the topic &amp; count above)
          </summary>
          <pre className="mt-2 text-xs bg-ink/5 border-2 border-ink/10 rounded-xl p-3 max-h-44 overflow-auto whitespace-pre-wrap text-slate-600">
            {buildPrompt()}
          </pre>
          <button onClick={copyPrompt} className="btn-primary text-sm mt-2">
            {promptCopied ? '✓ Copied' : 'Copy prompt'}
          </button>
        </details>
        <textarea
          value={rawJson}
          onChange={(e) => setRawJson(e.target.value)}
          className="w-full p-3 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none h-60 font-mono text-sm"
          placeholder={EXAMPLE}
        />
        {(parseError || error) && (
          <p className="text-tile-red font-medium" role="alert">{parseError || error}</p>
        )}
        {successMessage && <p className="text-tile-green-deep font-medium" role="alert">{successMessage}</p>}
        <div className="flex flex-col sm:flex-row gap-2">
          <button onClick={loadIntoEditor} disabled={!rawJson.trim()} className="btn-primary flex-1 disabled:opacity-50">
            Validate &amp; edit →
          </button>
          <button onClick={startBlank} className="btn-ghost flex-1 !text-ink !bg-ink/10 hover:!bg-ink/15">
            Or build from scratch
          </button>
        </div>
      </div>
    </div>
  );
};
